import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { postJSON } from "../lib/api";

const INTENT_COPY = {
    book_consultation: {
        title: "Book a 30-minute consultation",
        body: "Tell us a bit about your environment and we'll line up a senior engineer.",
        cta: "Request consultation",
        extraLabel: "Preferred date",
        extraKey: "preferred_date",
        extraType: "date",
    },
    request_quote: {
        title: "Get a tailored quote",
        body: "Share a few details and we'll come back with a clean proposal — usually within one business day.",
        cta: "Request quote",
        extraLabel: "Scope (1 line)",
        extraKey: "scope",
        extraType: "text",
    },
    enterprise_inquiry: {
        title: "Enterprise & MSP inquiry",
        body: "Multi-tenant, white-label, or large rollout? Tell us roughly how big.",
        cta: "Talk to sales",
        extraLabel: "Approx. team size",
        extraKey: "team_size",
        extraType: "text",
    },
};

export const LeadForm = ({ intent, sessionId, onClose }) => {
    const copy = INTENT_COPY[intent] || INTENT_COPY.book_consultation;
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [company, setCompany] = useState("");
    const [extra, setExtra] = useState("");
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (status === "loading") return;
        setStatus("loading");
        setError("");
        try {
            await postJSON("/leads", {
                session_id: sessionId,
                intent,
                name: name.trim(),
                email: email.trim(),
                company: company.trim(),
                extra: extra ? { [copy.extraKey]: extra } : {},
            });
            setStatus("success");
        } catch (err) {
            setStatus("error");
            setError(err.message || "Could not submit. Please try again.");
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 6, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="my-3 ml-8 rounded-xl border border-accent-cyan/25 bg-surface/90 backdrop-blur-md shadow-glow-cyan overflow-hidden"
            data-testid="lead-form"
            data-intent={intent}
        >
            {status === "success" ? (
                <div className="p-5 flex items-start gap-3" data-testid="lead-form-success">
                    <CheckCircle2 className="w-5 h-5 text-accent-mint mt-0.5 shrink-0" />
                    <div>
                        <p className="text-[14px] text-text-primary font-medium">
                            Got it. Our team will reach out within 24 hours.
                        </p>
                        <p className="text-[12px] text-text-muted mt-1">
                            You can close this and keep chatting.
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-3 text-[11px] font-mono uppercase tracking-[0.2em] text-text-muted hover:text-accent-cyan transition-colors duration-200"
                            data-testid="lead-form-close"
                        >
                            Close
                        </button>
                    </div>
                </div>
            ) : (
                <form onSubmit={submit} className="p-4 sm:p-5 space-y-3">
                    <div className="flex items-center justify-between">
                        <h3 className="text-[14px] font-semibold text-text-primary">
                            {copy.title}
                        </h3>
                        <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-accent-cyan/80">
                            {intent.replace(/_/g, " ")}
                        </span>
                    </div>
                    <p className="text-[12.5px] text-text-muted leading-relaxed">
                        {copy.body}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <input
                            type="text"
                            required
                            placeholder="Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2 text-[13px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 focus:bg-canvas transition-colors duration-200"
                            data-testid="lead-form-name"
                        />
                        <input
                            type="email"
                            required
                            placeholder="Work email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2 text-[13px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 focus:bg-canvas transition-colors duration-200"
                            data-testid="lead-form-email"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <input
                            type="text"
                            placeholder="Company"
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            className="bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2 text-[13px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 focus:bg-canvas transition-colors duration-200"
                            data-testid="lead-form-company"
                        />
                        <input
                            type={copy.extraType}
                            placeholder={copy.extraLabel}
                            value={extra}
                            onChange={(e) => setExtra(e.target.value)}
                            className="bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2 text-[13px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 focus:bg-canvas transition-colors duration-200"
                            data-testid="lead-form-extra"
                        />
                    </div>
                    {status === "error" && (
                        <div
                            className="flex items-center gap-2 text-[12px] text-accent-red bg-accent-red/5 border border-accent-red/20 rounded-md px-3 py-2"
                            data-testid="lead-form-error"
                        >
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            {error}
                        </div>
                    )}
                    <div className="flex items-center justify-between gap-3 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-[11px] font-mono uppercase tracking-[0.2em] text-text-dim hover:text-text-muted transition-colors duration-200"
                            data-testid="lead-form-dismiss"
                        >
                            Not now
                        </button>
                        <button
                            type="submit"
                            disabled={status === "loading"}
                            className="px-3.5 py-1.5 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan disabled:opacity-40 transition-shadow duration-200"
                            data-testid="lead-form-submit"
                        >
                            {status === "loading" ? "Sending…" : copy.cta}
                        </button>
                    </div>
                </form>
            )}
        </motion.div>
    );
};

export default LeadForm;
